import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import {
  Plus,
  Search,
  Edit2,
  Trash2,
  ChevronRight,
  ChevronDown,
  Filter,
  Download,
  X,
  Check,
  AlertCircle
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../../../components/ui/card";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Badge } from "../../../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select";
import { Label } from "../../../components/ui/label";
import { Switch } from "../../../components/ui/switch";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TIPOS_CUENTA = [
  { value: "ACTIVO", label: "Activo", color: "bg-blue-100 text-blue-700" },
  { value: "PASIVO", label: "Pasivo", color: "bg-red-100 text-red-700" },
  { value: "PATRIMONIO", label: "Patrimonio", color: "bg-purple-100 text-purple-700" },
  { value: "INGRESO", label: "Ingreso", color: "bg-green-100 text-green-700" },
  { value: "EGRESO", label: "Egreso", color: "bg-orange-100 text-orange-700" },
];

const PlanCuentas = ({ tenantId }) => {
  const [cuentas, setCuentas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filtroTipo, setFiltroTipo] = useState("todos");
  const [soloImputables, setSoloImputables] = useState(false);
  const [expandedItems, setExpandedItems] = useState({});
  
  // Modal states
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState("crear");
  const [cuentaEditando, setCuentaEditando] = useState(null);
  const [formData, setFormData] = useState({
    codigo: "",
    descripcion: "",
    tipo_cuenta: "ACTIVO",
    es_monetaria: true,
    requiere_centro_costo: false,
  });
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [saving, setSaving] = useState(false);

  const fetchCuentas = async () => {
    setLoading(true);
    try {
      let url = `${API_URL}/api/erp/empresas/${tenantId}/cuentas?incluir_inactivas=true`;
      if (filtroTipo !== "todos") url += `&tipo=${filtroTipo}`;
      if (soloImputables) url += `&solo_imputables=true`;
      
      const response = await axios.get(url);
      setCuentas(response.data);
    } catch (error) {
      console.error("Error al cargar cuentas:", error);
      toast.error("Error al cargar el plan de cuentas");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCuentas();
  }, [tenantId, filtroTipo, soloImputables]);

  const toggleExpand = (codigo) => {
    setExpandedItems(prev => ({ ...prev, [codigo]: !prev[codigo] }));
  };

  const handleOpenModal = (mode, cuenta = null) => {
    setModalMode(mode);
    if (cuenta) {
      setCuentaEditando(cuenta);
      setFormData({
        codigo: cuenta.codigo,
        descripcion: cuenta.descripcion,
        tipo_cuenta: cuenta.tipo_cuenta,
        es_monetaria: cuenta.es_monetaria,
        requiere_centro_costo: cuenta.requiere_centro_costo,
      });
    } else {
      setCuentaEditando(null);
      setFormData({
        codigo: "",
        descripcion: "",
        tipo_cuenta: "ACTIVO",
        es_monetaria: true,
        requiere_centro_costo: false,
      });
    }
    setModalOpen(true);
  };

  const handleSave = async () => {
    if (!formData.codigo || !formData.descripcion) {
      toast.error("Complete todos los campos requeridos");
      return;
    }

    setSaving(true);
    try {
      if (modalMode === "crear") {
        await axios.post(`${API_URL}/api/erp/empresas/${tenantId}/cuentas`, formData);
        toast.success("Cuenta creada exitosamente");
      } else {
        await axios.put(`${API_URL}/api/erp/empresas/${tenantId}/cuentas/${cuentaEditando.id}`, formData);
        toast.success("Cuenta actualizada exitosamente");
      }
      setModalOpen(false);
      fetchCuentas();
    } catch (error) {
      console.error("Error al guardar cuenta:", error);
      toast.error(error.response?.data?.detail || "Error al guardar la cuenta");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (cuenta) => {
    try {
      await axios.delete(`${API_URL}/api/erp/empresas/${tenantId}/cuentas/${cuenta.id}`);
      toast.success("Cuenta eliminada exitosamente");
      setDeleteConfirm(null);
      fetchCuentas();
    } catch (error) {
      console.error("Error al eliminar cuenta:", error);
      toast.error(error.response?.data?.detail || "Error al eliminar la cuenta");
    }
  };

  const handleInitPlan = async () => {
    try {
      const response = await axios.post(`${API_URL}/api/erp/empresas/${tenantId}/cuentas/inicializar-plan-basico`);
      toast.success(`Plan inicializado: ${response.data.cuentas_insertadas} cuentas creadas`);
      fetchCuentas();
    } catch (error) {
      toast.error("Error al inicializar el plan");
    }
  };

  // Filtrar cuentas por busqueda
  const cuentasFiltradas = cuentas.filter(cuenta => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return cuenta.codigo.toLowerCase().includes(term) || 
           cuenta.descripcion.toLowerCase().includes(term);
  });

  // Si hay busqueda activa, mostrar lista plana de resultados
  const isSearching = searchTerm.trim().length > 0;

  // Organizar cuentas por nivel (solo si NO hay busqueda)
  const organizarCuentas = () => {
    const nivel1 = cuentasFiltradas.filter(c => c.nivel === 1);
    return nivel1;
  };

  const getHijos = (codigoPadre) => {
    return cuentasFiltradas.filter(c => {
      const parentPrefix = codigoPadre + ".";
      return c.codigo.startsWith(parentPrefix) && 
             c.codigo.split(".").length === codigoPadre.split(".").length + 1;
    });
  };

  const renderCuenta = (cuenta, depth = 0) => {
    const hijos = getHijos(cuenta.codigo);
    const hasHijos = hijos.length > 0;
    const isExpanded = expandedItems[cuenta.codigo];
    const tipoConfig = TIPOS_CUENTA.find(t => t.value === cuenta.tipo_cuenta);

    return (
      <div key={cuenta.id}>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className={`flex items-center gap-2 p-3 hover:bg-gray-50 border-b border-gray-100 ${!cuenta.activa ? "opacity-50" : ""}`}
          style={{ paddingLeft: `${depth * 24 + 12}px` }}
        >
          {hasHijos ? (
            <button
              onClick={() => toggleExpand(cuenta.codigo)}
              className="p-1 hover:bg-gray-200 rounded"
              data-testid={`expand-${cuenta.codigo}`}
            >
              {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            </button>
          ) : (
            <div className="w-6" />
          )}
          
          <span className="font-mono text-sm text-gray-500 w-24">{cuenta.codigo}</span>
          <span className="flex-1 font-medium">{cuenta.descripcion}</span>
          
          <Badge className={tipoConfig?.color || "bg-gray-100"}>
            {tipoConfig?.label || cuenta.tipo_cuenta}
          </Badge>
          
          {cuenta.es_imputable && (
            <Badge variant="outline" className="text-xs">Imputable</Badge>
          )}
          
          {!cuenta.activa && (
            <Badge variant="destructive" className="text-xs">Inactiva</Badge>
          )}
          
          <div className="flex gap-1">
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={() => handleOpenModal("editar", cuenta)}
              data-testid={`edit-${cuenta.id}`}
            >
              <Edit2 className="w-4 h-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-red-500 hover:text-red-700"
              onClick={() => setDeleteConfirm(cuenta)}
              data-testid={`delete-${cuenta.id}`}
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        </motion.div>
        
        <AnimatePresence>
          {isExpanded && hijos.map(hijo => renderCuenta(hijo, depth + 1))}
        </AnimatePresence>
      </div>
    );
  };

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <Card className="cao-card">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Buscar por codigo o descripcion..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-cuentas"
              />
            </div>
            
            <Select value={filtroTipo} onValueChange={setFiltroTipo}>
              <SelectTrigger className="w-40" data-testid="filtro-tipo">
                <SelectValue placeholder="Tipo" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="todos">Todos</SelectItem>
                {TIPOS_CUENTA.map(tipo => (
                  <SelectItem key={tipo.value} value={tipo.value}>{tipo.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <div className="flex items-center gap-2">
              <Switch
                id="solo-imputables"
                checked={soloImputables}
                onCheckedChange={setSoloImputables}
              />
              <Label htmlFor="solo-imputables" className="text-sm">Solo imputables</Label>
            </div>
            
            <Button onClick={() => handleOpenModal("crear")} data-testid="btn-nueva-cuenta">
              <Plus className="w-4 h-4 mr-2" />
              Nueva Cuenta
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Lista de Cuentas */}
      <Card className="cao-card">
        <CardHeader className="pb-0">
          <div className="flex justify-between items-center">
            <CardTitle className="text-[#1E3876]">Plan de Cuentas</CardTitle>
            <span className="text-sm text-gray-500">{cuentasFiltradas.length} cuentas</span>
          </div>
        </CardHeader>
        <CardContent className="p-0 mt-4">
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2853B9]"></div>
            </div>
          ) : cuentasFiltradas.length === 0 ? (
            <div className="text-center py-12">
              <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600 mb-4">No hay cuentas registradas</p>
              <Button onClick={handleInitPlan} variant="outline">
                Inicializar Plan Basico
              </Button>
            </div>
          ) : (
            <div className="max-h-[600px] overflow-y-auto">
              {isSearching ? (
                // Vista plana cuando se busca
                cuentasFiltradas
                  .sort((a, b) => a.codigo.localeCompare(b.codigo))
                  .map(cuenta => {
                    const tipoConfig = TIPOS_CUENTA.find(t => t.value === cuenta.tipo_cuenta);
                    return (
                      <div
                        key={cuenta.id}
                        className={`flex items-center gap-2 p-3 hover:bg-gray-50 border-b border-gray-100 ${!cuenta.activa ? "opacity-50" : ""}`}
                      >
                        <div className="w-6" />
                        <span className="font-mono text-sm text-gray-500 w-28">{cuenta.codigo}</span>
                        <span className="flex-1 font-medium">{cuenta.descripcion}</span>
                        <Badge className={tipoConfig?.color || "bg-gray-100"}>
                          {tipoConfig?.label || cuenta.tipo_cuenta}
                        </Badge>
                        {cuenta.es_imputable && (
                          <Badge variant="outline" className="text-xs">Imputable</Badge>
                        )}
                        {!cuenta.activa && (
                          <Badge variant="destructive" className="text-xs">Inactiva</Badge>
                        )}
                        <div className="flex gap-1">
                          <Button variant="ghost" size="icon" className="h-8 w-8"
                            onClick={() => handleOpenModal("editar", cuenta)}>
                            <Edit2 className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500 hover:text-red-700"
                            onClick={() => setDeleteConfirm(cuenta)}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    );
                  })
              ) : (
                // Vista jerarquica normal
                organizarCuentas().map(cuenta => renderCuenta(cuenta))
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modal Crear/Editar */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="text-[#1E3876]">
              {modalMode === "crear" ? "Nueva Cuenta" : "Editar Cuenta"}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-4 gap-4">
              <div className="col-span-1">
                <Label htmlFor="codigo">Codigo *</Label>
                <Input
                  id="codigo"
                  value={formData.codigo}
                  onChange={(e) => setFormData({ ...formData, codigo: e.target.value })}
                  placeholder="1.1.01"
                  data-testid="input-codigo"
                />
              </div>
              <div className="col-span-3">
                <Label htmlFor="descripcion">Descripcion *</Label>
                <Input
                  id="descripcion"
                  value={formData.descripcion}
                  onChange={(e) => setFormData({ ...formData, descripcion: e.target.value })}
                  placeholder="Nombre de la cuenta"
                  data-testid="input-descripcion"
                />
              </div>
            </div>
            
            <div>
              <Label htmlFor="tipo_cuenta">Tipo de Cuenta</Label>
              <Select 
                value={formData.tipo_cuenta} 
                onValueChange={(v) => setFormData({ ...formData, tipo_cuenta: v })}
              >
                <SelectTrigger data-testid="select-tipo-cuenta">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TIPOS_CUENTA.map(tipo => (
                    <SelectItem key={tipo.value} value={tipo.value}>{tipo.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Switch
                  id="es_monetaria"
                  checked={formData.es_monetaria}
                  onCheckedChange={(v) => setFormData({ ...formData, es_monetaria: v })}
                />
                <Label htmlFor="es_monetaria">Cuenta Monetaria</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  id="requiere_cc"
                  checked={formData.requiere_centro_costo}
                  onCheckedChange={(v) => setFormData({ ...formData, requiere_centro_costo: v })}
                />
                <Label htmlFor="requiere_cc">Requiere Centro de Costo</Label>
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setModalOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSave} disabled={saving} data-testid="btn-guardar-cuenta">
              {saving ? "Guardando..." : "Guardar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirm Delete Dialog */}
      <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-red-600">Eliminar Cuenta</DialogTitle>
          </DialogHeader>
          <p className="py-4">
            Esta seguro de eliminar la cuenta <strong>{deleteConfirm?.codigo} - {deleteConfirm?.descripcion}</strong>?
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirm(null)}>
              Cancelar
            </Button>
            <Button variant="destructive" onClick={() => handleDelete(deleteConfirm)}>
              Eliminar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PlanCuentas;
